import { Box, Button, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import IconMi from 'components/common/icon/IconMi';
import React, { useEffect, useState } from 'react';
import { NodeModel } from '@minoru/react-dnd-treeview/dist/types';
import { FolderData } from 'api/editor/element';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import styles from './FolderSelect.styles';
import Tree from 'components/common/tree/Tree';
import useBundleTranslation from 'i18n';
import Node from './Node';

export default function AddFolderButton({
    selectedFolders,
    availableFolders,
    onFolderAdd,
}: {
    selectedFolders: FolderData[];
    availableFolders: NodeModel[];
    onFolderAdd: (folderId: number) => void;
}) {
    const { t } = useBundleTranslation(['app/editor/folder/content']);

    const [showFoldersPopup, setShowFoldersPopup] = useState(false);
    const [foldersFilter, setFoldersFilter] = useState('');
    const [folders4select, setFolders4select] = useState<NodeModel[]>([]);
    const [openNodes, setOpenNodes] = useState<Set<string | number>>();

    useEffect(() => {
        if (availableFolders.length > 0) {
            const openNodesSet = new Set<string | number>();
            availableFolders.forEach((folder) => {
                openNodesSet.add(folder.id);
            });

            setOpenNodes(openNodesSet);
            setFolders4select(availableFolders);
        }
    }, [availableFolders]);

    const onAddButtonClick = () => {
        setShowFoldersPopup(true);
    };

    const popupSettings: PopupSettings = {
        title: t('choose_folder_title'),
        noButtons: true,
    };

    const handleHidePopup = () => {
        setShowFoldersPopup(false);
        setFoldersFilter('');
        setFolders4select(availableFolders);
    };

    const getFilterInputIcon = () => {
        if (foldersFilter === '') {
            return <IconMi icon="search" fontSize={'16'} />;
        }

        return (
            <Box
                sx={{ cursor: 'pointer', marginTop: '5px', color: 'text.primary' }}
                onClick={() => {
                    setFoldersFilter('');
                }}
            >
                <IconMi icon="times" fontSize={'16'} />
            </Box>
        );
    };

    const handleChangeFilterFolders = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFoldersFilter(event.target.value);
    };

    const pushParentFolders = (folder: NodeModel, filteredFoldersWithParents: NodeModel[]) => {
        if (Number(folder.parent) > 0) {
            const parentExist = filteredFoldersWithParents.find((searchFolder) => {
                return searchFolder.id === folder.parent;
            });

            if (!parentExist) {
                const parent = availableFolders.find((searchFolder) => {
                    return searchFolder.id === folder.parent;
                });

                if (parent) {
                    filteredFoldersWithParents.push(parent);
                    pushParentFolders(parent, filteredFoldersWithParents);
                }
            }
        }
    };

    const pushFolderChild = (folder: NodeModel, filteredFoldersWithParents: NodeModel[]) => {
        const childFolders = availableFolders.filter((searchFolder) => {
            return searchFolder.parent === folder.id;
        });

        if (childFolders.length > 0) {
            childFolders.forEach((childFolder) => {
                const childExist = filteredFoldersWithParents.find((searchFolder) => {
                    return searchFolder.id === childFolder.id;
                });

                if (!childExist) {
                    filteredFoldersWithParents.push(childFolder);
                    pushFolderChild(childFolder, filteredFoldersWithParents);
                }
            });
        }
    };

    useEffect(() => {
        if (foldersFilter === '') {
            setFolders4select(availableFolders);

            return;
        }

        const filteredFolders = availableFolders.filter((folder) => {
            return folder.text.toLowerCase().includes(foldersFilter.toLowerCase());
        });

        let filteredFoldersWithParents: NodeModel[] = [];

        if (filteredFolders.length > 0) {
            filteredFolders.forEach((folder) => {
                const folderExist = filteredFoldersWithParents.find((searchFolder) => {
                    return searchFolder.id === folder.id;
                });
                if (!folderExist) {
                    filteredFoldersWithParents.push(folder);
                    pushParentFolders(folder, filteredFoldersWithParents);
                }
            });
            filteredFolders.forEach((folder) => {
                pushFolderChild(folder, filteredFoldersWithParents);
            });
        }

        setFolders4select(filteredFoldersWithParents);
    }, [foldersFilter, availableFolders]);

    return (
        <>
            <Button
                data-test={'add-folder-button'}
                color={'success'}
                variant={'light'}
                startIcon={<IconMi icon="new" fontSize="16" />}
                onClick={onAddButtonClick}
            >
                {t('add_folder')}
            </Button>
            {showFoldersPopup && (
                <Popup settings={popupSettings} open={showFoldersPopup} onHide={handleHidePopup} maxWidth={'popupLg'}>
                    <Stack direction={'column'} spacing={2}>
                        <TextField
                            value={foldersFilter}
                            placeholder={t('filter_folders_placeholder')}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ color: 'primary.main' }}>
                                        {getFilterInputIcon()}
                                    </InputAdornment>
                                ),
                            }}
                            fullWidth
                            onChange={handleChangeFilterFolders}
                        />
                        <Box sx={styles.wrapper}>
                            {folders4select.length > 0 ? (
                                <Tree
                                    treeData={folders4select}
                                    nodeComponent={Node}
                                    openState={openNodes}
                                    additionalNodeProps={{
                                        selectedFolders: selectedFolders,
                                        onFolderSelect: (folder: NodeModel) => {
                                            onFolderAdd(Number(folder.id));
                                            handleHidePopup();
                                        },
                                    }}
                                />
                            ) : (
                                <Box sx={styles.noResultsWrapper}>
                                    <Typography>{t('folders_no_result')}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Stack>
                </Popup>
            )}
        </>
    );
}
